import { useState } from "react";
import { Plus } from "lucide-react";
import api from "@/lib/api";
import Dialog from "./Dialog";

/**
 * Botón "Añadir reparación" en la vista de detalle de un documento.
 *
 * Al pulsarlo abre un diálogo con buscador que llama a
 * `GET /repairs?q=...&limit=20`. Cuando el usuario elige una reparación,
 * invoca `onPick(repair)` para que el padre cree el bloque correspondiente.
 *
 * Las reparaciones ya añadidas (cuyos ids vienen en `existingRepairIds`)
 * aparecen deshabilitadas con la etiqueta "Ya añadida".
 *
 * Extraído de `pages/Billing.jsx` en el refactor preventivo de iteración 66.
 */
export default function AddRepairButton({ existingRepairIds, onPick }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const search = async (val) => {
    setQ(val);
    if (!val.trim()) { setResults([]); return; }
    setLoading(true);
    try {
      const r = await api.get(`/repairs?q=${encodeURIComponent(val)}&limit=20`);
      setResults(r.data || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="docview-add-repair-btn"
        className="bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-3 py-2 text-xs uppercase tracking-wider inline-flex items-center gap-1"
      >
        <Plus size={12} /> Añadir reparación
      </button>
      {open && (
        <Dialog onClose={() => setOpen(false)} title="Añadir reparación" wide>
          <input
            autoFocus
            value={q}
            onChange={(e) => search(e.target.value)}
            placeholder="Buscar por código, cliente, equipo o nº serie..."
            data-testid="docview-add-repair-search"
            className="w-full ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm"
          />
          <div className="mt-3 max-h-80 overflow-y-auto border border-[#E5E5E0]">
            {loading && <div className="p-3 text-xs text-[#525252]">Buscando...</div>}
            {!loading && results.length === 0 && q && (
              <div className="p-3 text-xs text-[#525252]">Sin resultados.</div>
            )}
            {results.map((r) => {
              const already = existingRepairIds.includes(r.id);
              return (
                <button
                  key={r.id}
                  type="button"
                  disabled={already}
                  onClick={() => { onPick(r); setOpen(false); setQ(""); setResults([]); }}
                  data-testid={`docview-add-repair-pick-${r.code}`}
                  className="w-full text-left px-3 py-2 border-b border-[#E5E5E0] last:border-0 hover:bg-[#F0F4E8] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="font-mono text-xs font-semibold">{r.code}</div>
                      <div className="text-[10px] text-[#525252]">
                        {r.customer_name} · {r.device_type} {r.model} {r.serial && `· ${r.serial}`}
                      </div>
                    </div>
                    {already && <span className="text-[9px] uppercase font-mono text-[#525252]">Ya añadida</span>}
                  </div>
                </button>
              );
            })}
          </div>
        </Dialog>
      )}
    </>
  );
}
